/**
 * benefitsPackages.js
 * --------------------------------------------------------------------------
 * Structured, monetised benefits data per tenant. Each tenant has a benefits
 * package that reflects the country / industry norms:
 *
 *   - Germany: statutory healthcare top-up, betriebliche Altersvorsorge (bAV),
 *     Jobticket, vermögenswirksame Leistungen, etc.
 *   - Switzerland: 3-pillar pension, Krankenkasse subsidy, REKA cheques
 *   - Spain: Sanitas private healthcare, ticket restaurant, Plan de pensiones
 *   - UK: private medical insurance, salary-sacrifice pension, EV car scheme
 *
 * Each item has:
 *   - id: stable key
 *   - i18nKey: translation lookup for the user-facing label
 *   - icon: lucide icon name
 *   - annualValueEur: monetary value of the benefit per year (employer cost
 *     equivalent, which is what the worker would have to replace if they
 *     took the role elsewhere)
 *   - category: 'health' | 'retirement' | 'mobility' | 'lifestyle' | 'equity' | 'protection'
 *   - description i18n key (optional)
 *
 * The total benefits value is summed and added to the Total Compensation
 * figure so workers see the full package, not just base + variable.
 */

const tenantPackages = {
  // Default package (used as fallback)
  default: {
    description: 'Standard EU benefits package',
    items: [
      { id: 'healthInsurance',         i18nKey: 'employeePortal.benefitsPkg.healthInsurance',          icon: 'HeartPulse',     annualValueEur: 4800, category: 'health' },
      { id: 'dentalCoverage',          i18nKey: 'employeePortal.benefitsPkg.dentalCoverage',           icon: 'Smile',          annualValueEur: 600,  category: 'health' },
      { id: 'pension',                 i18nKey: 'employeePortal.benefitsPkg.pension',                  icon: 'PiggyBank',      annualValueEur: 4500, category: 'retirement' },
      { id: 'lifeInsurance',           i18nKey: 'employeePortal.benefitsPkg.lifeInsurance',            icon: 'Shield',         annualValueEur: 350,  category: 'protection' },
      { id: 'professionalDevelopment', i18nKey: 'employeePortal.benefitsPkg.professionalDevelopment',  icon: 'GraduationCap',  annualValueEur: 1500, category: 'lifestyle' },
      { id: 'wellness',                i18nKey: 'employeePortal.benefitsPkg.wellnessProgram',          icon: 'Heart',          annualValueEur: 480,  category: 'lifestyle' },
    ],
  },

  // Global Consultancy DE
  'global-consultancy-de': {
    description: 'Senior consulting package, Germany',
    items: [
      { id: 'privateHealth',           i18nKey: 'employeePortal.benefitsPkg.privateHealth',            icon: 'HeartPulse',     annualValueEur: 6800,  category: 'health' },
      { id: 'pensionBav',              i18nKey: 'employeePortal.benefitsPkg.pensionBav',               icon: 'PiggyBank',      annualValueEur: 7200,  category: 'retirement' },
      { id: 'companyCar',              i18nKey: 'employeePortal.benefitsPkg.companyCar',               icon: 'Car',            annualValueEur: 12000, category: 'mobility' },
      { id: 'jobticket',               i18nKey: 'employeePortal.benefitsPkg.jobticket',                icon: 'Train',          annualValueEur: 1200,  category: 'mobility' },
      { id: 'mealAllowance',           i18nKey: 'employeePortal.benefitsPkg.mealAllowance',            icon: 'Utensils',       annualValueEur: 1320,  category: 'lifestyle' },
      { id: 'professionalDevelopment', i18nKey: 'employeePortal.benefitsPkg.professionalDevelopment',  icon: 'GraduationCap',  annualValueEur: 3000,  category: 'lifestyle' },
      { id: 'lifeInsurance',           i18nKey: 'employeePortal.benefitsPkg.lifeInsurance',            icon: 'Shield',         annualValueEur: 850,   category: 'protection' },
      { id: 'wellness',                i18nKey: 'employeePortal.benefitsPkg.wellnessProgram',          icon: 'Heart',          annualValueEur: 600,   category: 'lifestyle' },
    ],
  },

  // Consumer Goods DE
  'consumer-goods-de': {
    description: 'Standard FMCG package, Germany',
    items: [
      { id: 'statutoryHealthTopUp',    i18nKey: 'employeePortal.benefitsPkg.statutoryHealthTopUp',     icon: 'HeartPulse',     annualValueEur: 1800,  category: 'health' },
      { id: 'pensionBav',              i18nKey: 'employeePortal.benefitsPkg.pensionBav',               icon: 'PiggyBank',      annualValueEur: 3600,  category: 'retirement' },
      { id: 'jobticket',               i18nKey: 'employeePortal.benefitsPkg.jobticket',                icon: 'Train',          annualValueEur: 950,   category: 'mobility' },
      { id: 'vwLeistungen',            i18nKey: 'employeePortal.benefitsPkg.vwLeistungen',             icon: 'Wallet',         annualValueEur: 480,   category: 'retirement' },
      { id: 'productAllowance',        i18nKey: 'employeePortal.benefitsPkg.productAllowance',         icon: 'ShoppingBag',    annualValueEur: 600,   category: 'lifestyle' },
      { id: 'mealAllowance',           i18nKey: 'employeePortal.benefitsPkg.mealAllowance',            icon: 'Utensils',       annualValueEur: 1100,  category: 'lifestyle' },
      { id: 'professionalDevelopment', i18nKey: 'employeePortal.benefitsPkg.professionalDevelopment',  icon: 'GraduationCap',  annualValueEur: 1500,  category: 'lifestyle' },
      { id: 'wellness',                i18nKey: 'employeePortal.benefitsPkg.wellnessProgram',          icon: 'Heart',          annualValueEur: 480,   category: 'lifestyle' },
    ],
  },

  // Global Bank ES
  'global-bank-es': {
    description: 'Banking package, Spain',
    items: [
      { id: 'sanitasPrivateHealth',    i18nKey: 'employeePortal.benefitsPkg.sanitasPrivateHealth',     icon: 'HeartPulse',     annualValueEur: 3200,  category: 'health' },
      { id: 'planPensiones',           i18nKey: 'employeePortal.benefitsPkg.planPensiones',            icon: 'PiggyBank',      annualValueEur: 4800,  category: 'retirement' },
      { id: 'ticketRestaurant',        i18nKey: 'employeePortal.benefitsPkg.ticketRestaurant',         icon: 'Utensils',       annualValueEur: 2100,  category: 'lifestyle' },
      { id: 'transportAllowance',      i18nKey: 'employeePortal.benefitsPkg.transportAllowance',       icon: 'Train',          annualValueEur: 1320,  category: 'mobility' },
      { id: 'lifeInsurance',           i18nKey: 'employeePortal.benefitsPkg.lifeInsurance',            icon: 'Shield',         annualValueEur: 600,   category: 'protection' },
      { id: 'employeeStockPlan',       i18nKey: 'employeePortal.benefitsPkg.employeeStockPlan',        icon: 'TrendingUp',     annualValueEur: 4500,  category: 'equity' },
      { id: 'professionalDevelopment', i18nKey: 'employeePortal.benefitsPkg.professionalDevelopment',  icon: 'GraduationCap',  annualValueEur: 2000,  category: 'lifestyle' },
      { id: 'mortgageRateBenefit',     i18nKey: 'employeePortal.benefitsPkg.mortgageRateBenefit',      icon: 'Home',           annualValueEur: 1800,  category: 'lifestyle' },
    ],
  },

  // Global Manufacturer CH
  'global-manufacturer-ch': {
    description: 'Industrial package, Switzerland',
    items: [
      { id: 'krankenkasseSubsidy',     i18nKey: 'employeePortal.benefitsPkg.krankenkasseSubsidy',      icon: 'HeartPulse',     annualValueEur: 4200,  category: 'health' },
      { id: 'pillar2Pension',          i18nKey: 'employeePortal.benefitsPkg.pillar2Pension',           icon: 'PiggyBank',      annualValueEur: 9600,  category: 'retirement' },
      { id: 'pillar3Subsidy',          i18nKey: 'employeePortal.benefitsPkg.pillar3Subsidy',           icon: 'Vault',          annualValueEur: 1500,  category: 'retirement' },
      { id: 'rekaCheck',               i18nKey: 'employeePortal.benefitsPkg.rekaCheck',                icon: 'Ticket',         annualValueEur: 600,   category: 'lifestyle' },
      { id: 'shiftAllowance',          i18nKey: 'employeePortal.benefitsPkg.shiftAllowance',           icon: 'Clock',          annualValueEur: 3200,  category: 'lifestyle' },
      { id: 'companyCar',              i18nKey: 'employeePortal.benefitsPkg.companyCar',               icon: 'Car',            annualValueEur: 8400,  category: 'mobility' },
      { id: 'lifeInsurance',           i18nKey: 'employeePortal.benefitsPkg.lifeInsurance',            icon: 'Shield',         annualValueEur: 720,   category: 'protection' },
      { id: 'professionalDevelopment', i18nKey: 'employeePortal.benefitsPkg.professionalDevelopment',  icon: 'GraduationCap',  annualValueEur: 2400,  category: 'lifestyle' },
    ],
  },

  // Global Security UK
  'global-security-uk': {
    description: 'Services package, United Kingdom',
    items: [
      { id: 'privateMedicalUk',        i18nKey: 'employeePortal.benefitsPkg.privateMedicalUk',         icon: 'HeartPulse',     annualValueEur: 1850,  category: 'health' },
      { id: 'pensionSalarySacrifice',  i18nKey: 'employeePortal.benefitsPkg.pensionSalarySacrifice',   icon: 'PiggyBank',      annualValueEur: 4100,  category: 'retirement' },
      { id: 'evCarScheme',             i18nKey: 'employeePortal.benefitsPkg.evCarScheme',              icon: 'Car',            annualValueEur: 5400,  category: 'mobility' },
      { id: 'cycleToWork',             i18nKey: 'employeePortal.benefitsPkg.cycleToWork',              icon: 'Bike',           annualValueEur: 480,   category: 'mobility' },
      { id: 'lifeInsuranceUk',         i18nKey: 'employeePortal.benefitsPkg.lifeInsuranceUk',          icon: 'Shield',         annualValueEur: 420,   category: 'protection' },
      { id: 'incomeProtection',        i18nKey: 'employeePortal.benefitsPkg.incomeProtection',         icon: 'Umbrella',       annualValueEur: 600,   category: 'protection' },
      { id: 'employeeStockPlan',       i18nKey: 'employeePortal.benefitsPkg.employeeStockPlan',        icon: 'TrendingUp',     annualValueEur: 2600,  category: 'equity' },
      { id: 'professionalDevelopment', i18nKey: 'employeePortal.benefitsPkg.professionalDevelopment',  icon: 'GraduationCap',  annualValueEur: 1200,  category: 'lifestyle' },
      { id: 'wellness',                i18nKey: 'employeePortal.benefitsPkg.wellnessProgram',          icon: 'Heart',          annualValueEur: 360,   category: 'lifestyle' },
    ],
  },
};

export function getBenefitsPackage(tenantId) {
  return tenantPackages[tenantId] || tenantPackages.default;
}

export function getTotalBenefitsValue(tenantId) {
  const pkg = getBenefitsPackage(tenantId);
  return pkg.items.reduce((sum, item) => sum + (item.annualValueEur || 0), 0);
}

/** Group benefits by category for display. */
export function groupBenefitsByCategory(items) {
  const groups = {};
  items.forEach((item) => {
    if (!groups[item.category]) groups[item.category] = [];
    groups[item.category].push(item);
  });
  return groups;
}

export const CATEGORY_ORDER = ['health', 'retirement', 'mobility', 'protection', 'equity', 'lifestyle'];
